import type { ComponentProps } from 'react';
import { useNavigate } from '@tanstack/react-router';

/**
 * The way out of a subpage: the back arrow, and the Close beside it.
 *
 * <p>A real anchor, so it can be middle-clicked, opened in a new tab and copied, that navigates in
 * place on an ordinary click. Not a `<Link>`, because the target is a built href rather than a
 * route path with typed search (see `backTarget`), and `Link` requires the path. Going through
 * `navigate` is what keeps the unsaved-changes blocker in front of it.</p>
 */
export function BackLink({ href, onClick, ...props }: ComponentProps<'a'> & { href: string }) {
    const navigate = useNavigate();
    return (
        <a
            {...props}
            href={href}
            onClick={(event) => {
                onClick?.(event);
                // The browser keeps the clicks that mean somewhere else: a new tab, a new window,
                // a download. Everything else is this page going back.
                if (
                    event.defaultPrevented ||
                    event.button !== 0 ||
                    event.metaKey ||
                    event.ctrlKey ||
                    event.shiftKey ||
                    event.altKey
                ) {
                    return;
                }
                event.preventDefault();
                void navigate({ href });
            }}
        />
    );
}
